import type { GapRunValidation, RunGap } from '@momentum/types';
import { GapRunVerdict } from '@momentum/types';

import {
  SCORE_LABEL,
  SCORE_ORDER,
  scoreDeltas,
  type ScoreAxis,
  type ScoreDeltaDirection,
} from './scores';

/**
 * The derivations behind the run report — the view that renders a `RunGap` as
 * the pipeline wrote it.
 *
 * Everything here is derived from fields the run already carries. The report
 * generates no prose and rewrites no value: where the run said nothing, the
 * report says nothing.
 */

/** One scoring axis as the report's header row states it. */
export interface ReportScore {
  axis: ScoreAxis;
  label: string;
  /** What stands: the validator's revision when adjudicated, else the finder's. */
  value: string;
  /** The finder's value, present only when the validator moved the axis. */
  revisedFrom?: string;
  /** `'none'` both when the validator left the axis alone and when it never ran. */
  direction: ScoreDeltaDirection;
}

/**
 * The four axes with the revision folded in.
 *
 * The revised value leads because it is the one that stands — a reviewer acting
 * on `missability` after adjudication is acting on the validator's number, not
 * the finder's. The finder's is kept beside it rather than dropped: the size of
 * the move is the clearest signal the report carries about how well the claim
 * survived checking.
 *
 * An unadjudicated gap yields the finder's own four scores with no direction,
 * which is honest — nothing revised them, so nothing about them moved.
 */
export function reportScores(
  source: RunGap,
  validation: GapRunValidation | undefined
): ReportScore[] {
  if (validation === undefined) {
    return SCORE_ORDER.map((axis) => ({
      axis,
      label: SCORE_LABEL[axis],
      value: source[axis],
      direction: 'none' as const,
    }));
  }

  return scoreDeltas(source, validation).map((delta) => ({
    axis: delta.axis,
    label: SCORE_LABEL[delta.axis],
    value: delta.after,
    ...(delta.direction === 'none' ? {} : { revisedFrom: delta.before }),
    direction: delta.direction,
  }));
}

/** A standing warning over the finder's `doNow`, derived from the verdict. */
export interface DoNowWarning {
  tone: 'amber' | 'rose';
  text: string;
}

/**
 * Whether the finder's immediate action still stands after adjudication.
 *
 * `doNow` is written by the finder, before the validator rules — so on a gap the
 * validator knocked down, the action on screen is advice written against a claim
 * that no longer holds. Nothing rewrites it (the run is frozen, and inventing a
 * replacement action would be the report authoring pipeline output), so the only
 * honest move is to say so above it.
 *
 * `CONTRADICTED` reads harder than `WEAKENED` because the two rulings differ in
 * kind: a weakened claim is still a claim, a contradicted one has evidence
 * against it. Every other verdict — including an absent one — leaves the action
 * as the finder wrote it, with no banner: `UNVERIFIABLE` means the validator
 * could not settle the claim, not that it fell.
 */
export function doNowWarning(
  validation: GapRunValidation | undefined
): DoNowWarning | undefined {
  if (validation === undefined) return undefined;

  if (validation.verdict === GapRunVerdict.CONTRADICTED) {
    return {
      tone: 'rose',
      text: 'The validator contradicted this gap. The action below was written by the finder beforehand, against a claim the evidence argues against — read the verdict before acting on any of it.',
    };
  }

  if (validation.verdict === GapRunVerdict.WEAKENED) {
    return {
      tone: 'amber',
      text: 'The validator weakened this gap. The action below was written by the finder beforehand and has not been revised since the ruling.',
    };
  }

  return undefined;
}
